import { useEffect, useState } from "react";
import { getUsers } from "../../services/userService";

// One screenful of a picker: a school's list usually arrives whole, a
// network's never does. Past it the picker says how many are left and search
// narrows on the server — the old `page_size: MAX_PAGE_SIZE` fetch rendered up
// to a thousand rows and silently dropped everyone after them.
export const PICKER_PAGE_SIZE = 50;

// Alphabetical, with the unique username last: page N+1 has to continue page
// N, and ordering on a non-unique column lets two namesakes swap across the
// page boundary — one listed twice, the other never.
const PICKER_ORDERING = "last_name,first_name,username";

// `/auth/users/` for a picker, a page at a time. `params` are the list filters
// (`observable`, `can_conduct`, `search`, `organization`); a change to any of
// them starts over from page 1, and `loadMore` appends the next page.
//
// status: "loading" (first page) | "ready" | "loadingMore" | "moreFailed" |
// "error" (first page failed — not the same thing as nobody matching).
export default function usePagedUsers(params, { enabled = true } = {}) {
  // Callers build `params` inline, so its serialized form is what identifies
  // the query: a new object carrying the same filters is not a new request.
  const query = JSON.stringify(params);
  const [pageOf, setPageOf] = useState({ query, page: 1 });
  // Follows the query during render rather than in an effect, so a new search
  // never fires one request for the stale page before the one for page 1. And
  // it must be STORED, not just derived: clearing a search returns to a query
  // this state still remembers, and a derived page would resume at page 3 and
  // append it to the search results, losing pages 1 and 2.
  if (pageOf.query !== query) setPageOf({ query, page: 1 });
  const page = pageOf.query === query ? pageOf.page : 1;
  const [retryKey, setRetryKey] = useState(0);
  const [state, setState] = useState({
    users: [],
    count: 0,
    hasMore: false,
    status: "loading",
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, status: page === 1 ? "loading" : "loadingMore" }));
    getUsers({
      params: {
        ...JSON.parse(query),
        page,
        page_size: PICKER_PAGE_SIZE,
        ordering: PICKER_ORDERING,
      },
      signal: controller.signal,
    })
      .then((data) => {
        const rows = (data?.results || []).filter((u) => u.is_active);
        setState((s) => {
          const kept = page === 1 ? [] : s.users;
          const seen = new Set(kept.map((u) => u.uuid));
          const users = kept.concat(rows.filter((u) => !seen.has(u.uuid)));
          return {
            users,
            count: data?.count ?? users.length,
            hasMore: Boolean(data?.next),
            status: "ready",
          };
        });
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        // A later page that failed keeps what is already listed; the same
        // button then retries THAT page rather than skipping past it.
        setState((s) =>
          page === 1
            ? { users: [], count: 0, hasMore: false, status: "error" }
            : { ...s, status: "moreFailed" },
        );
      });
    return () => controller.abort();
  }, [query, page, enabled, retryKey]);

  const retry = () => setRetryKey((k) => k + 1);
  const loadMore =
    state.status === "moreFailed" ? retry : () => setPageOf({ query, page: page + 1 });

  return { ...state, loadMore, retry };
}
